import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import ApartmentIcon from '@mui/icons-material/Apartment';
import clsx from 'clsx';
import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { hasOneOfPermission, hasPermission, Permissions } from '../../../../store/reducers/user';
import { getAdmissionStartDate } from '../../../../util/patientUtil';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AttendanceTimelineHistory from '../../../../components/ui/timeline/attendanceTimelineHistory';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import usePatientLocation from '../../../../hooks/usePatientLocation';
import { ctxSplitScreen } from '../../../../store/reducers/patientContext';
import moment from 'moment';
import useDict from '../../../../hooks/useDict';
import AppointmentTimelineHistory from '../../../../components/ui/timeline/appointmentTimelineHistory';
import { MAP_TYPE } from '../../../../util/mapCreator/mapUtils';
import PatientLocationHistory from './patientLocationHistory';
import { getConfig } from '../../../../util/configUtil';
import { SLOT_STATUSES } from '../../../appointments/utils/constants/appointmentSessionConstants';

const useStyles = makeStyles((theme) => ({
    attendanceListItem: {
        width: '100%',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: theme.palette.inPatient.formBackground,
        marginBottom: 10,
    },
    attendanceHeader: {
        backgroundColor: theme.palette.inPatient.inputBackground,
        padding: '5px 10px',
        '&.canceled': {
            backgroundColor: '#E25B61',
        },
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        '&.right': {
            marginLeft: 'auto',
        },
    },
    label: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        marginRight: 5,
        fontSize: 14,
    },
    headerText: {
        color: theme.palette.admissionForm.labelColor,
        '&.canceled': {
            color: 'unset',
        },
    },
    location: {
        backgroundColor: '#03C3EC',
        borderRadius: 3,
        padding: '0 5px',
        color: '#222425',
        fontWeight: 600,
        fontSize: 'small',
        display: 'flex',
        alignItems: 'center',
        height: 35,
        minWidth: 155,
        justifyContent: 'center',
    },
}));

const PatientActivityBox = ({ admission, expandFromParent, onOpen, disabledAdmission }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();

    const [location, loadingLocations] = usePatientLocation(admission);
    const [hospitals] = useDict('hospitals');

    const splitScreen = useSelector(ctxSplitScreen);
    const isSSEnabled = splitScreen?.enabled;

    const [expand, setExpand] = useState(false);
    const [activityColor, setActivityColor] = useState('');
    const [activityText, setActivityText] = useState('');
    const isAppointment = admission?.__type === 'com.fortrus.unity.api.model.appointments.AppointmentSlot';

    const inpatientEnabled = getConfig('modules.inpatient.enabled', false);
    const isCanceled = !isAppointment && admission?.admission?.canceled;

    const canView = dispatch(
        hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_ATTENDANCE_HISTORY, Permissions.AGYLE_CAN_EDIT_ATTENDANCE_HISTORY]),
    );
    const canEnterCanceled = dispatch(hasPermission(Permissions.AGYLE_CAN_SEE_CANCELED));

    useEffect(() => {
        let color, text;
        if (isAppointment) {
            switch (admission?.status) {
                case SLOT_STATUSES.DNA.value:
                case SLOT_STATUSES.WNB.value:
                    color = '#E25B61';
                    text = 'Not Arrived';
                    break;
                default:
                    color = '#B75BE2';
                    text = 'Future Appointment';
                    break;
            }
        } else {
            switch (admission?.admission?.attendanceType) {
                case 'E':
                    let split = admission?.admission?.pointOfCare ? admission?.admission?.pointOfCare?.split('_')?.[0] : 'ED';
                    switch (split) {
                        case MAP_TYPE.SDEC:
                            color = theme.palette.attendanceType.sdec;
                            text = t('Same Day Emergency Care');
                            break;
                        case MAP_TYPE.UTC:
                            color = theme.palette.attendanceType.utc;
                            text = t('Urgent Treatment Centre');
                            break;
                        case MAP_TYPE.ED:
                        default:
                            color = theme.palette.attendanceType.ed;
                            text = t('Emergency Department');
                    }
                    break;
                case 'I':
                    color = theme.palette.attendanceType.ip;
                    text = t('Inpatient');
                    break;
                case 'O':
                    color = theme.palette.attendanceType.op;
                    text = t('Outpatient');
                    break;
                default:
                    console.warn('Wrong attendance type', admission);
                    color = '#222222';
                    text = 'Unknown type';
                    break;
            }
        }
        setActivityColor(color);
        setActivityText(text);
    }, [admission, theme]);

    useEffect(() => {
        setExpand(expandFromParent);
    }, [expandFromParent]);

    const getHeader = () => {
        let facility = null;
        const startDate = admission?.admission ? getAdmissionStartDate(admission?.admission) : moment(admission?.startDate);
        const age = startDate?.diff(admission?.patient?.dob, 'years');

        const hospitalCode = admission?.hospitalId || admission?.admission?.facility;
        if (hospitalCode) {
            const hospital = _.find(hospitals, (hos) => hos?.__id === hospitalCode);
            if (hospital) {
                facility = (
                    <div className={classes.divRow} style={{ gap: 5 }}>
                        <ApartmentIcon />

                        <span>{_.startCase(hospital?.description?.toLowerCase()) || 'UNK'}</span>
                    </div>
                );
            }
        }

        const isDisabled = () => {
            let typeCheck = false;
            if (isCanceled) return !canEnterCanceled;
            const attType = admission?.admission?.attendanceType;
            if (attType === 'I' || attType === 'IP') {
                typeCheck = !inpatientEnabled;
            }
            return disabledAdmission || !canView || typeCheck;
        };

        return (
            <div
                className={clsx(classes.divRow, classes.attendanceHeader, isCanceled && 'canceled')}
                style={{ gap: 10, flexWrap: isSSEnabled ? 'wrap' : 'nowrap' }}
            >
                <Typography variant="body1">{activityText || ''}</Typography>

                {facility && <div className={clsx(classes.divRow)}>{facility}</div>}

                <span className={clsx(classes.headerText, isCanceled && 'canceled')}>{admission?.admission?.attendanceId || '-'}</span>

                <span className={clsx(classes.headerText, isCanceled && 'canceled')}>
                    {t('Age at visit:')} {age}
                </span>

                {isCanceled && (
                    <Typography variant="subtitle1" fontWeight={600}>
                        {t('ACTIVITY CANCELLED')}
                    </Typography>
                )}

                <div className={clsx(classes.divRow, 'right')} style={{ gap: 10 }}>
                    {!isAppointment && !loadingLocations && location?.__id && (
                        <div className={classes.location}>{location?.trudDescription}</div>
                    )}

                    {!isAppointment && !isCanceled && (
                        <PatientLocationHistory patient={admission} disabled={disabledAdmission || !canView || admission?.__id === null} />
                    )}

                    {!isAppointment && (
                        <AgyleButton
                            variant="contained"
                            disabled={isDisabled()}
                            onClick={() => onOpen(admission, admission?.__id === null)}
                        >
                            {t('Review')}
                        </AgyleButton>
                    )}

                    <IconButton onClick={() => setExpand(!expand)}>{expand ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </div>
            </div>
        );
    };

    return (
        <div className={classes.attendanceListItem} style={{ borderLeft: `5px solid ${activityColor}` }}>
            {getHeader()}

            {isAppointment && <AppointmentTimelineHistory expanded={expand} appointment={admission} />}
            {!isAppointment && <AttendanceTimelineHistory admission={admission} expanded={expand} />}
        </div>
    );
};

export default PatientActivityBox;
